import React from 'react';
import type { ThresholdSegment } from '../overviewCard';
import { TONE_BY_ZONE, type OverviewTone } from '../overviewMeta';
import { useOverviewMetric } from '../useOverviewMetric';

export type CashRunwayData = {
    tone: OverviewTone;
    days: number;
    cashTetri: number;
    dailyFixedCostTetri: number;
    deltaDays: number | null;
};

/**
 * The runway scale, straight from the mock's axis: 0 · 30 · 90 · 180+.
 *
 * These are drawing instructions, not policy. The server decides which band a
 * reading falls in; this only decides where the bar changes colour, and the two
 * agree because they are the same numbers written down twice — once as a
 * judgement, once as a picture.
 */
const DANGER_DAYS = 30;
const WARNING_DAYS = 90;
export const RUNWAY_SCALE_DAYS = 180;

export const RUNWAY_SEGMENTS: ThresholdSegment[] = [
    { tone: 'danger', ratio: DANGER_DAYS / RUNWAY_SCALE_DAYS },
    {
        tone: 'warning',
        ratio: (WARNING_DAYS - DANGER_DAYS) / RUNWAY_SCALE_DAYS,
    },
    {
        tone: 'success',
        ratio: (RUNWAY_SCALE_DAYS - WARNING_DAYS) / RUNWAY_SCALE_DAYS,
    },
];

/** Where a reading sits on the bar. Anything past the axis pins to its end. */
export const runwayMarker = (days: number): number =>
    Math.min(days, RUNWAY_SCALE_DAYS) / RUNWAY_SCALE_DAYS;

/**
 * Cash runway: how many days the business survives on what it holds, at the rate
 * it currently spends.
 */
export const useCashRunway = () => {
    const { metric, error, isLoading, missing, onRetry } =
        useOverviewMetric('cashRunway');

    const data = React.useMemo<CashRunwayData | null>(() => {
        if (!metric || metric.state !== 'COMPUTED') {
            return null;
        }
        const { tone, days, cashTetri, dailyFixedCostTetri } = metric;
        if (
            tone === null ||
            days === null ||
            cashTetri === null ||
            dailyFixedCostTetri === null
        ) {
            return null;
        }
        return {
            tone: TONE_BY_ZONE[tone],
            days,
            cashTetri,
            dailyFixedCostTetri,
            deltaDays: metric.deltaDays,
        };
    }, [metric]);

    // Stub: the expense-category flow arrives with the Expenses tab.
    const onEmptyAction = React.useCallback(() => {}, []);

    return { data, error, isLoading, missing, onRetry, onEmptyAction };
};
